import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { ChildrenIcon, DeleteIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { genderEnumMapping, toGenderEnumValue } from '../../../lib/types/enums/common/gender.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { HostFamilyChildInput } from '../../../lib/types/models/host-family-child/host-family-child-input'
import { IdInput } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../elements/action-table-wrapper'
import HostFamilyChildForm from './host-family-child-form'

const HostFamilyChildrenTableForm = () => {
  const [index, setIndex] = useState<number>()
  const genderMapping = genderEnumMapping()

  const form = useFormContext<HostFamilyInput>()

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    HostFamilyChildInput
  >(['create', 'edit', 'delete'])

  const children = form.watch('children') || []

  const columns: Column<IdInput<HostFamilyChildInput>>[] = [
    {
      enableSorting: true,
      header: 'Last name',
      accessorKey: 'lastName'
    },
    {
      enableSorting: true,
      header: 'First name',
      accessorKey: 'firstName'
    },
    {
      enableSorting: true,
      header: 'Gender',
      accessorKey: 'gender',
      cell: ({ row }) => genderMapping[toGenderEnumValue(row.original.gender)]
    },
    {
      enableSorting: true,
      header: 'Date of birth',
      accessorKey: 'birthDate',
      cell: ({ row }) => <TableDate date={row.original.birthDate} />
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              setIndex(row.index)
              toggleModal('delete')
            }}
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'Children'}
      actions={[
        <Button
          key={'add'}
          type="button"
          icon={PlusIcon}
          label={'Add a child'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      {children.length > 0 && (
        <DataTable<IdInput<HostFamilyChildInput>>
          manualSorting={false}
          columns={columns}
          data={children.map((child, index) => ({ ...child, id: index }))}
          rowCount={children.length}
          paginated={false}
        />
      )}

      <Modal
        isOpen={modalState.create}
        title={'Add a child'}
        size="medium"
        hideActions
        icon={() => <ChildrenIcon color={tailwindColors.secondary.DEFAULT} />}
        onCancel={() => closeModel('create')}
      >
        <HostFamilyChildForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isEnglish: true,
            onCancel: () => closeModel('create'),
            onConfirm: (value) => {
              form.setValue('children', [...children, value])
              closeModel('create')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.edit}
        title={toFullName(selectedData?.firstName, selectedData?.lastName)}
        hideActions
        icon={() => <ChildrenIcon color={tailwindColors.secondary.DEFAULT} />}
        size="medium"
        onCancel={() => closeModel('edit')}
      >
        <HostFamilyChildForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: (values) => {
              form.setValue(
                'children',
                children.map((child) => (child.id === values.id ? values : child))
              )
              closeModel('edit')
            }
          })}
        />
      </Modal>

      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            form.setValue(
              'children',
              children.filter((_, i) => i !== index)
            )
            closeModel('delete')
          }
        }}
        onCancel={() => toggleModal('delete')}
        isOpen={modalState.delete}
        title={'Are you sure you want to delete this child information?'}
        icon={DeleteIcon}
        variant={'destructive'}
      />
    </ActionTableWrapper>
  )
}

export default HostFamilyChildrenTableForm
